import React, { useEffect, useState } from "react";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import { Badge } from "../../../Component/AdminComponents/Badge";
import { format } from "date-fns";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { Plus, ShieldCheck, Upload } from "lucide-react";
import BlueButton from "../../../Component/Common/BlueButton";
import CategoryModal from "./CategoryModal";
import { BASE_URL } from "../../../config/api";
import SearchInput from "../../../Component/Common/SearchInput";
import AddButton from "../../../Component/Common/AddButton";
import UpdateButton from "../../../Component/Common/UpdateButton";
import StatusButton from "../../../Component/Common/StatusButton";
import SimpleTable from "../../../Component/Common/SimpleTable";
import axios from "axios";

export default function CategoriesView() {
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // ----------------- FETCH API -----------------
  const fetchCategories = async () => {

    try {

      setLoading(
        true
      );

      setError(
        ""
      );

      const response =
        await axios.get(
          `${BASE_URL}warranty/get-categories`,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const data =
        response.data;

      setCategories(
        data
      );

      setFiltered(
        data
      );

    } catch (err) {

      setError(
        err?.response?.data
          ?.message ||
        err?.message ||
        "Something went wrong"
      );

    } finally {

      setLoading(
        false
      );

    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ----------------- SEARCH FILTER -----------------
  useEffect(() => {
    const result = categories.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(result);
  }, [search, categories]);

  // ----------------- HANDLE SUBMIT -----------------
  const handleSubmit = () => {
    setIsOpen(false);
    setSelectedCategory(null);
    fetchCategories(); // Refresh list after submit
  };

  // ----------------- HANDLE ADD / UPDATE -----------------
  const handleAdd = () => {
    setSelectedCategory(null);
    setIsOpen(true);
  };

  const handleUpdate = (category) => {
    setSelectedCategory(category);
    setIsOpen(true);
  };

  // ----------------- TOGGLE STATUS -----------------
  const toggleStatus = async (
    category
  ) => {

    try {

      await axios.post(
        `${BASE_URL}warranty/category/toggle-status/${category.id}`,
        {},
        {
          headers: {
            Accept:
              "application/json",

            "Content-Type":
              "application/json",
          },
        }
      );

      fetchCategories();

    } catch (err) {

      console.error(
        err
      );

      alert(
        err?.response?.data
          ?.message ||
        err?.message ||
        "Something went wrong"
      );

    }
  };

  const categoryRows = filtered.map((cat) => {
  const isActive = cat.status === 1;

  return {
    raw: cat, // 🔥 full object for actions

    cells: [
      // Image
      {
        title: (
          <div className="flex justify-center">
            <img
              src={cat.image || "/placeholder.png"}
              alt={cat.name}
              className="h-10 w-10 rounded-lg object-cover border"
            />
          </div>
        ),
      },
      // Name
      {
        title: cat.name,
      },

      // Status Badge
      {
        title: (
          <Badge color={isActive ? "green" : "red"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
        ),
      },

      // Created
      {
        title: cat.created_at
          ? format(new Date(cat.created_at), "dd MMM yyyy")
          : "—",
      },

      // Updated
      {
        title: cat.updated_at
          ? format(new Date(cat.updated_at), "dd MMM yyyy")
          : "—",
      },
    ],
  };
});

  return (
      <div className="space-y-4">
        <SectionCard title="Warranty Categories">
          {/* RIGHT SIDE BUTTONS */}
          <div className="flex flex-wrap justify-end mb-3 gap-2">
            <SearchInput
              value={search}
              onChange={setSearch}
              onClear={() => setSearch("")}
              placeholder="Search category..."
              size="sm"
            />
            <AddButton onClick={handleAdd} />
            <div className="hidden lg:block">
              <RefreshButton onClick={fetchCategories} />
            </div>
          </div>
          <CategoryModal
            isOpen={isOpen}
            onClose={() => { setIsOpen(false); setSelectedCategory(null); }}
            onSubmit={handleSubmit}
            initialData={selectedCategory}
          />
          {/* LOADER */}
          {loading && <RegularLoader />}
          {/* ERROR */}
          {error && (
            <div className="text-center py-4 text-red-500 text-sm">{error}</div>
          )}

          {/* TABLE */}
          {!loading && !error && (
            <>
            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-4">
              {filtered.map((cat) => {
                const isActive = cat.status === 1;

                return (
                  <div
                    key={cat.id}
                    className="bg-white rounded-2xl p-4 shadow-[0_6px_18px_rgba(0,0,0,0.06)] border border-slate-300"
                  >
                    <div className="flex items-start gap-4">
                      {/* Image */}
                      <div className="h-16 w-16 rounded-2xl bg-yellow-100 overflow-hidden shrink-0">
                        <img
                          src={cat.image || "/placeholder.png"}
                          alt={cat.name}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-sm font-semibold text-slate-900 truncate">
                              {cat.name}
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Created:{" "}
                              {cat.created_at
                                ? format(new Date(cat.created_at), "dd MMM yyyy")
                                : "—"}
                            </p>
                          </div>

                          <Badge color={isActive ? "green" : "red"}>
                            {isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        <div className="mt-2 text-xs text-slate-500">
                          Updated:{" "}
                          {cat.updated_at
                            ? format(new Date(cat.updated_at), "dd MMM yyyy")
                            : "—"}
                        </div>

                        {/* Actions */}
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={() => handleUpdate(cat)}
                            className="flex-1 rounded-xl bg-blue-600 py-2 text-xs font-semibold text-white hover:bg-blue-700 transition"
                          >
                            Update
                          </button>

                          <button
                            onClick={() => toggleStatus(cat)}
                            className={`flex-1 rounded-xl py-2 text-xs font-semibold transition ${
                              isActive
                                ? "bg-rose-100 text-rose-700 hover:bg-rose-200"
                                : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            }`}
                          >
                            {isActive ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filtered.length === 0 && !loading && (
                <div className="text-center text-sm text-slate-500 py-6">
                  No categories found.
                </div>
              )}
            </div>
          <div className="hidden md:block">
          <SimpleTable
            headers={["Image", "Name", "Status", "Created", "Updated"]}
            rows={categoryRows}
            renderActions={(cat) => {
              const isActive = cat.status === 1;
              const buttonLabel = isActive ? "Inactive" : "Active";

              return (
                <div className="flex items-center justify-center gap-2">
                  <UpdateButton onClick={() => handleUpdate(cat)} />

                  <StatusButton
                    label={buttonLabel}
                    onClick={() => toggleStatus(cat)}
                  />
                </div>
              );
            }}
            showSrNo
            pageSize={10}
          />
          </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}


// import React, { useEffect, useState } from "react";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { format } from "date-fns";
// import RegularLoader from "../../../Component/Loader/RegularLoader";

// export default function CategoriesView() {
//   const [categories, setCategories] = useState([]);
//   const [filtered, setFiltered] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [search, setSearch] = useState("");

//   // ----------------- FETCH API -----------------
//   const fetchCategories = async () => {
//     try {
//       setLoading(true);
//       setError("");

//       const res = await fetch("https://goelectronix.in/api/v1/warranty/get-categories");
//       if (!res.ok) throw new Error("Failed to fetch categories");

//       const data = await res.json();
//       setCategories(data);
//       setFiltered(data);
//     } catch (err) {
//       setError(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchCategories();
//   }, []);

//   // ----------------- SEARCH FILTER -----------------
//   useEffect(() => {
//     const result = categories.filter((c) =>
//       c.name.toLowerCase().includes(search.toLowerCase())
//     );
//     setFiltered(result);
//   }, [search, categories]);

//   return (
//     <div className="space-y-4">
//       <SectionCard title="Warranty Categories">

//         {/* RIGHT SIDE BUTTONS */}
//         <div className="flex justify-between mb-3">
//           {/* Search bar */}
//           <input
//             type="text"
//             placeholder="Search category..."
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             className="border text-xs px-3 py-1.5 rounded-lg w-40 bg-slate-50"
//           />

//           <RefreshButton onClick={fetchCategories} />
//         </div>

//         {/* Loading */}
//         {loading && (
//           <RegularLoader/>
//         )}

//         {/* Error */}
//         {error && (
//           <div className="text-center py-4 text-red-500 text-sm">
//             {error}
//           </div>
//         )}

//         {/* TABLE */}
//         {!loading && !error && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-xs">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 text-left">Image</th>
//                   <th className="py-2 text-left">Name</th>
//                   <th className="py-2 text-left">Status</th>
//                   <th className="py-2 text-left">Created</th>
//                   <th className="py-2 text-left">Updated</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {filtered.map((cat) => (
//                   <tr
//                     key={cat.id}
//                     className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                   >
//                     <td className="py-2">
//                       <img
//                         src={cat.image}
//                         alt={cat.name}
//                         className="h-10 w-10 rounded-lg object-cover border"
//                       />
//                     </td>

//                     <td className="py-2 text-slate-700 font-semibold">
//                       {cat.name}
//                     </td>

//                     <td className="py-2">
//                       <Badge color={cat.status === 1 ? "green" : "red"}>
//                         {cat.status === 1 ? "Active" : "Inactive"}
//                       </Badge>
//                     </td>

//                     <td className="py-2 text-[10px] text-slate-600">
//                       {cat.created_at
//                         ? format(new Date(cat.created_at), "dd MMM yyyy")
//                         : "—"}
//                     </td>

//                     <td className="py-2 text-[10px] text-slate-600">
//                       {cat.updated_at
//                         ? format(new Date(cat.updated_at), "dd MMM yyyy")
//                         : "—"}
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>

//             {/* No Data */}
//             {filtered.length === 0 && (
//               <p className="text-center text-xs text-slate-500 py-4">
//                 No categories found.
//               </p>
//             )}
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }
